import { useState } from "react";
import { motion } from "framer-motion";
import { ArrowRight, Target, XCircle, CheckCircle, Clock } from "lucide-react";
import { Navbar } from "@/components/layout/Navbar";
import { Footer } from "@/components/layout/Footer";
import { SEOHead } from "@/components/SEOHead";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const whatWeEvaluate = [
  "Scalability potential",
  "Founder dependency risk",
  "Operator readiness",
  "Buyer-grade positioning",
];

const whatThisIsNot = [
  "Not a consultation request",
  "Not coaching",
  "Not personality-based guidance",
  "Not implementation support",
];

const reviewOutcomes = [
  {
    icon: CheckCircle,
    status: "Approved",
    description: "Proceed to engagement discussions",
    color: "text-green-400",
    bgColor: "bg-green-500/10",
  },
  {
    icon: Clock,
    status: "Hold",
    description: "Nurture until readiness criteria met",
    color: "text-yellow-400",
    bgColor: "bg-yellow-500/10",
  },
  {
    icon: XCircle,
    status: "Declined",
    description: "Not a fit at this time",
    color: "text-red-400",
    bgColor: "bg-red-500/10",
  },
];

const AuthorityReviewPage = () => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    company: "",
    revenue_model: "",
    operator_count: "",
    founder_involvement: "",
    authority_reason: "",
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      await axios.post(`${API}/contact`, {
        name: formData.name,
        email: formData.email,
        company: formData.company,
        revenue: formData.revenue_model,
        message: `Primary Revenue Model: ${formData.revenue_model}\nCurrent Operator Count: ${formData.operator_count}\nFounder Involvement Level: ${formData.founder_involvement}\n\nWhy Authority Matters:\n${formData.authority_reason}`,
      });
      setIsSubmitted(true);
      toast.success("Application submitted successfully.");
    } catch (error) {
      toast.error("Failed to submit. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const revenueModels = [
    "Service-based",
    "Licensing / Certification",
    "Consulting",
    "Hybrid",
    "Other",
  ];

  const operatorCounts = [
    "0 (Founder only)",
    "1-3",
    "4-10",
    "10+",
  ];

  const founderInvolvementLevels = [
    "High — Founder required for most operations",
    "Medium — Founder involved in key decisions",
    "Low — Systems run with minimal founder input",
  ];

  if (isSubmitted) {
    return (
      <div className="min-h-screen bg-[#080808]" data-testid="authority-review-submitted">
        <Navbar />
        <section className="pt-32 pb-24 md:pt-40 md:pb-32 px-6 md:px-12 min-h-screen flex items-center">
          <div className="max-w-2xl mx-auto text-center">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
            >
              <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-8">
                <Target className="h-8 w-8 text-primary" strokeWidth={1.5} />
              </div>
              <h1 className="font-serif text-4xl md:text-5xl font-light text-foreground mb-6">
                Application<br />
                <span className="text-primary">Received</span>
              </h1>
              <p className="text-lg font-light leading-relaxed text-muted-foreground mb-4">
                Your application has been submitted for internal authority review.
              </p>
              <p className="text-sm text-muted-foreground mb-8">
                Applicants receive an <span className="text-green-400">approval</span>, 
                <span className="text-yellow-400"> hold</span>, or 
                <span className="text-red-400"> decline</span> decision.
              </p>
              <p className="text-xs text-muted-foreground">
                Submission does not guarantee acceptance.
              </p>
            </motion.div>
          </div>
        </section>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#080808]" data-testid="authority-review-page">
      <SEOHead 
        title="Authority Review Application"
        description="This review determines eligibility. Authority Reviews determine whether an operator, platform, or founder is eligible for system buildout."
        url="https://onpointauthority.com/authority-review"
      />
      <Navbar />

      {/* Hero Section */}
      <section className="pt-32 pb-12 md:pt-40 md:pb-16 px-6 md:px-12" data-testid="review-hero">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="max-w-3xl"
          >
            <p className="text-xs font-medium tracking-[0.2em] uppercase text-muted-foreground mb-6">
              Application
            </p>
            <h1 className="font-serif text-5xl md:text-6xl font-light tracking-tight leading-[0.95] text-foreground mb-8">
              Request an<br />
              <span className="text-primary">Authority Review</span>
            </h1>
            <p className="text-lg font-light leading-relaxed text-muted-foreground">
              This is not a consultation request. Authority Reviews determine whether an operator, 
              platform, or founder is eligible for system buildout.
            </p>
          </motion.div>
        </div>
      </section>

      {/* What We Evaluate Section */}
      <section className="py-16 md:py-24 px-6 md:px-12 border-t border-white/5" data-testid="what-is-section">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
            {/* What We Evaluate */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
            >
              <p className="text-xs font-medium tracking-[0.2em] uppercase text-primary mb-4">
                What We Evaluate
              </p>
              <ul className="space-y-3">
                {whatWeEvaluate.map((item, index) => (
                  <li key={index} className="flex items-center gap-3 text-foreground">
                    <div className="w-1.5 h-1.5 bg-primary rounded-full" />
                    {item}
                  </li>
                ))}
              </ul>
            </motion.div>

            {/* What This Is NOT */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8, delay: 0.1 }}
            >
              <p className="text-xs font-medium tracking-[0.2em] uppercase text-muted-foreground mb-4">
                What This Is NOT
              </p>
              <ul className="space-y-3">
                {whatThisIsNot.map((item, index) => (
                  <li key={index} className="flex items-center gap-3 text-muted-foreground">
                    <XCircle className="w-4 h-4 text-red-400/50" strokeWidth={1.5} />
                    {item}
                  </li>
                ))}
              </ul>
            </motion.div>

            {/* Review Outcomes */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8, delay: 0.2 }}
            >
              <p className="text-xs font-medium tracking-[0.2em] uppercase text-muted-foreground mb-4">
                Outcome
              </p>
              <div className="space-y-4">
                {reviewOutcomes.map((outcome, index) => (
                  <div key={index} className={`p-4 ${outcome.bgColor} border border-white/5`}>
                    <div className="flex items-center gap-3 mb-1">
                      <outcome.icon className={`w-4 h-4 ${outcome.color}`} strokeWidth={1.5} />
                      <span className={`font-medium ${outcome.color}`}>{outcome.status}</span>
                    </div>
                    <p className="text-xs text-muted-foreground ml-7">{outcome.description}</p>
                  </div>
                ))}
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Application Form */}
      <section className="py-16 md:py-24 px-6 md:px-12 bg-[#050505]" data-testid="review-form-section">
        <div className="max-w-4xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="mb-12 text-center"
          >
            <h2 className="font-serif text-3xl md:text-4xl font-normal tracking-tight text-foreground mb-4">
              Submit<br />
              <span className="text-primary">Application</span>
            </h2>
            <p className="text-sm text-muted-foreground">
              This review determines eligibility. Submission does not guarantee acceptance.
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, delay: 0.2 }}
          >
            <form onSubmit={handleSubmit} className="space-y-8" data-testid="authority-review-form">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-8">
                <div>
                  <label className="text-xs font-medium tracking-[0.15em] uppercase text-muted-foreground mb-3 block">
                    Full Name *
                  </label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    className="w-full bg-transparent border-b border-white/20 focus:border-primary px-0 py-4 text-foreground transition-colors"
                    placeholder="Your name"
                    data-testid="review-input-name"
                  />
                </div>
                <div>
                  <label className="text-xs font-medium tracking-[0.15em] uppercase text-muted-foreground mb-3 block">
                    Email Address *
                  </label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    className="w-full bg-transparent border-b border-white/20 focus:border-primary px-0 py-4 text-foreground transition-colors"
                    placeholder="your@email.com"
                    data-testid="review-input-email"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-8">
                <div>
                  <label className="text-xs font-medium tracking-[0.15em] uppercase text-muted-foreground mb-3 block">
                    Company / Asset Name *
                  </label>
                  <input
                    type="text"
                    name="company"
                    value={formData.company}
                    onChange={handleChange}
                    required
                    className="w-full bg-transparent border-b border-white/20 focus:border-primary px-0 py-4 text-foreground transition-colors"
                    placeholder="Company or asset name"
                    data-testid="review-input-company"
                  />
                </div>
                <div>
                  <label className="text-xs font-medium tracking-[0.15em] uppercase text-muted-foreground mb-3 block">
                    Primary Revenue Model *
                  </label>
                  <select
                    name="revenue_model"
                    value={formData.revenue_model}
                    onChange={handleChange}
                    required
                    className="w-full bg-transparent border-b border-white/20 focus:border-primary px-0 py-4 text-foreground transition-colors appearance-none cursor-pointer"
                    data-testid="review-select-revenue"
                  >
                    <option value="" className="bg-[#080808]">Select model</option>
                    {revenueModels.map((option) => (
                      <option key={option} value={option} className="bg-[#080808]">
                        {option}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-8">
                <div>
                  <label className="text-xs font-medium tracking-[0.15em] uppercase text-muted-foreground mb-3 block">
                    Current Operator Count *
                  </label>
                  <select
                    name="operator_count"
                    value={formData.operator_count}
                    onChange={handleChange}
                    required
                    className="w-full bg-transparent border-b border-white/20 focus:border-primary px-0 py-4 text-foreground transition-colors appearance-none cursor-pointer"
                    data-testid="review-select-operator-count"
                  >
                    <option value="" className="bg-[#080808]">Select count</option>
                    {operatorCounts.map((option) => (
                      <option key={option} value={option} className="bg-[#080808]">
                        {option}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="text-xs font-medium tracking-[0.15em] uppercase text-muted-foreground mb-3 block">
                    Founder Involvement Level *
                  </label>
                  <select
                    name="founder_involvement"
                    value={formData.founder_involvement}
                    onChange={handleChange}
                    required
                    className="w-full bg-transparent border-b border-white/20 focus:border-primary px-0 py-4 text-foreground transition-colors appearance-none cursor-pointer"
                    data-testid="review-select-founder-involvement"
                  >
                    <option value="" className="bg-[#080808]">Select level</option>
                    {founderInvolvementLevels.map((option) => (
                      <option key={option} value={option} className="bg-[#080808]">
                        {option}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div>
                <label className="text-xs font-medium tracking-[0.15em] uppercase text-muted-foreground mb-3 block">
                  Why authority matters to your exit or scale *
                </label>
                <textarea
                  name="authority_reason"
                  value={formData.authority_reason}
                  onChange={handleChange}
                  required
                  rows={4}
                  className="w-full bg-transparent border-b border-white/20 focus:border-primary px-0 py-4 text-foreground transition-colors resize-none"
                  placeholder="Describe why authority infrastructure matters for your scale or exit goals..."
                  data-testid="review-textarea-reason"
                />
              </div>

              <div className="pt-4 text-center">
                <Button
                  type="submit"
                  disabled={isSubmitting}
                  className="bg-primary text-primary-foreground hover:bg-primary/90 uppercase tracking-[0.15em] text-xs font-bold px-10 py-6 rounded-none gold-glow disabled:opacity-50"
                  data-testid="review-submit-button"
                >
                  {isSubmitting ? "Submitting..." : "Submit Application"}
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </div>
            </form>
          </motion.div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default AuthorityReviewPage;
